import { Stream } from 'interfaces'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Observable, Subscription, from } from 'rxjs'
import { getCuratedStreams, getSavedSources } from 'services/content/recommended/actions'
import { curatedStreamsSelector, userStreamsSelector } from 'services/content/selectors'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { SECTION_URL_INDUSTRIES, SECTION_URL_MY_STREAMS, SECTION_URL_POPULAR } from '../../home.config'
import CuratedStreamsCarousel from './CuratedStreamsCarousel'

interface CuratedStreamsCarouselContainerOwnProps {
  category: 'popular' | 'industries' | 'my-streams'
  navPrefix?: string
}

interface CuratedStreamsCarouselContainerConnectedProps {
  streams: {
    popular: Stream[]
    industries: Stream[]
    'my-streams': Stream[]
  }
  title?: string
  getPopular: () => Observable<any>
  getIndustries: () => Observable<any>
  getMyStreams: () => Observable<any>
}

type CuratedStreamsCarouselContainerProps = CuratedStreamsCarouselContainerConnectedProps & CuratedStreamsCarouselContainerOwnProps

const CuratedStreamsCarouselContainer = memo(function (props: CuratedStreamsCarouselContainerProps) {
  const { category, getPopular, getIndustries, getMyStreams } = props
  const [loading, setLoading] = useState(true)
  const [userStreams, setUserStreams] = useState<Stream[]>(props.streams['my-streams'])
  // EXPL: read streams via a ref so the fetch effect triggers on `category` only — getMyStreams
  // updates the store's streams, so listing props.streams as a dependency would re-fetch in a loop.
  const streamsRef = useRef(props.streams)
  useEffect(() => {
    streamsRef.current = props.streams
  })
  const stopLoading = useCallback(() => {
    setLoading(false)
  }, [])

  useEffect(() => {
    setLoading(true)
    let sub: Subscription
    switch (category) {
      case 'popular':
        sub = getPopular().subscribe(stopLoading)
        break
      case 'industries':
        sub = getIndustries().subscribe(stopLoading)
        break
      case 'my-streams':
        sub = getMyStreams().subscribe(response => {
          stopLoading()
          const streams = response.map((s: Stream) => {
            const userStream = streamsRef.current['my-streams'].find(str => str.id === s.id)
            return {
              ...s,
              ...userStream
            }
          })
          setUserStreams(streams)
        })
        break
      default:
        stopLoading()
    }
    return () => {
      if (sub) {
        sub.unsubscribe()
      }
    }
  }, [category, getPopular, getIndustries, getMyStreams, stopLoading])

  let navPrefix
  let streams = props.streams[props.category]

  switch (props.category) {
    case 'popular':
      navPrefix = SECTION_URL_POPULAR
      break
    case 'industries':
      navPrefix = SECTION_URL_INDUSTRIES
      break
    case 'my-streams':
      navPrefix = SECTION_URL_MY_STREAMS
      streams = userStreams
      break
  }

  navPrefix = props.navPrefix || navPrefix

  return (
    <CuratedStreamsCarousel
      category={props.category}
      streams={streams}
      navPrefix={navPrefix}
      title={props.title}
      loading={loading}
    />
  )
})

function mapStateToProps(state: StoreState) {
  const curatedStreams = curatedStreamsSelector(state)
  const userStreams = userStreamsSelector(state) as Stream[]

  return {
    streams: {
      ...curatedStreams,
      'my-streams': userStreams
    }
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    getPopular: () => from(dispatch(getCuratedStreams('popular')).unwrap()),
    getIndustries: () => from(dispatch(getCuratedStreams('industries')).unwrap()),
    getMyStreams: () => from(dispatch(getSavedSources()).unwrap())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CuratedStreamsCarouselContainer)
